import { useState } from 'react';
import {
  useProjectDomains,
  useCreateProjectDomain,
  useDeleteProjectDomain,
  useVerifyProjectDomain,
  useSetPrimaryProjectDomain,
  DomainVerifyError,
} from '../../hooks/useProjectDomains';
import { ConfirmDialog } from '../common/ConfirmDialog';
import type {
  IdpCallbackUrl,
  ProjectDomain,
  VerificationRecord,
  VerifyDiagnostic,
} from '../../types/projectDomain';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type BadgeTone,
} from '../../ui';
import './DomainsSettings.css';

// Stable docs URL we point users at from the verify-failure panel. Lives at
// the brand domain (apps/docs deploy target). Update both this and the
// Docusaurus config if the host changes.
const CUSTOM_DOMAINS_DOCS_URL = 'https://docs.pryma.dev/guides/custom-domains';

interface DomainsSettingsProps {
  projectId: string;
  userRole?: 'viewer' | 'member' | 'admin' | 'owner';
}

type ModalState = { type: 'none' } | { type: 'create' } | { type: 'delete'; domain: ProjectDomain };

export function DomainsSettings({ projectId, userRole = 'member' }: DomainsSettingsProps) {
  const { data: domains, isLoading, error } = useProjectDomains(projectId);
  const createDomain = useCreateProjectDomain();
  const deleteDomain = useDeleteProjectDomain();
  const verifyDomain = useVerifyProjectDomain();
  const setPrimary = useSetPrimaryProjectDomain();

  const [modalState, setModalState] = useState<ModalState>({ type: 'none' });
  const [flash, setFlash] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  // Per-domain verify failure detail. Cleared once the user re-runs verify
  // (or the domain row remounts). Distinct from `flash` because diagnostics
  // are sticky and tied to a specific row, not a transient toast.
  const [verifyFailures, setVerifyFailures] = useState<
    Record<string, { message: string; diagnostics: VerifyDiagnostic[] }>
  >({});

  const canManage = userRole === 'admin' || userRole === 'owner';

  const showFlash = (kind: 'success' | 'error', text: string) => {
    setFlash({ kind, text });
    setTimeout(() => setFlash(null), 4000);
  };

  const handleVerify = async (domainId: string) => {
    setVerifyFailures(prev => {
      if (!(domainId in prev)) return prev;
      const next = { ...prev };
      delete next[domainId];
      return next;
    });
    try {
      await verifyDomain.mutateAsync({ projectId, domainId });
      showFlash('success', 'Domain verified — SSL provisioning has started.');
    } catch (err) {
      const diagnostics = err instanceof DomainVerifyError ? err.diagnostics : [];
      const message = (err as Error).message;
      setVerifyFailures(prev => ({ ...prev, [domainId]: { message, diagnostics } }));
      showFlash('error', message);
    }
  };

  const handleSetPrimary = async (domainId: string) => {
    try {
      await setPrimary.mutateAsync({ projectId, domainId });
      showFlash('success', 'Primary domain updated.');
    } catch (err) {
      showFlash('error', (err as Error).message);
    }
  };

  const handleDelete = async (domainId: string) => {
    try {
      await deleteDomain.mutateAsync({ projectId, domainId });
      setModalState({ type: 'none' });
      showFlash('success', 'Domain removed.');
    } catch (err) {
      showFlash('error', (err as Error).message);
    }
  };

  if (isLoading) return <div className="domains-settings__loading">Loading domains…</div>;
  if (error) {
    return (
      <div className="domains-settings__error">
        Failed to load domains: {(error as Error).message}
      </div>
    );
  }

  return (
    <div className="domains-settings">
      <div className="domains-settings__header">
        <div>
          <h2>Custom Domains</h2>
          <p className="domains-settings__muted">
            Use your own branded auth domain (e.g. <code>auth.yourcompany.com</code>) so end users
            see your brand, not ours.
          </p>
        </div>
        {canManage && <Button onClick={() => setModalState({ type: 'create' })}>Add Domain</Button>}
      </div>

      {flash && (
        <div className={`domains-settings__flash domains-settings__flash--${flash.kind}`}>
          {flash.text}
        </div>
      )}

      {domains && domains.length === 0 && (
        <EmptyState
          title="No custom domains yet."
          description="Add a domain and we'll walk you through the DNS records to verify ownership."
        />
      )}

      {domains && domains.length > 0 && (
        <ul className="domains-settings__list">
          {domains.map(d => (
            <DomainRow
              key={d.id}
              domain={d}
              canManage={canManage}
              isVerifying={verifyDomain.isPending}
              verifyFailure={verifyFailures[d.id]}
              onVerify={() => handleVerify(d.id)}
              onSetPrimary={() => handleSetPrimary(d.id)}
              onDelete={() => setModalState({ type: 'delete', domain: d })}
            />
          ))}
        </ul>
      )}

      {modalState.type === 'create' && (
        <CreateDomainModal
          onClose={() => setModalState({ type: 'none' })}
          onCreate={data => createDomain.mutateAsync({ projectId, data })}
          isSubmitting={createDomain.isPending}
        />
      )}

      {modalState.type === 'delete' && (
        <ConfirmDialog
          title="Remove domain?"
          message={`This will remove ${modalState.domain.domain}. End users can no longer sign in via this host.`}
          confirmLabel="Remove"
          onConfirm={() => handleDelete(modalState.domain.id)}
          onCancel={() => setModalState({ type: 'none' })}
        />
      )}
    </div>
  );
}

interface DomainRowProps {
  domain: ProjectDomain;
  canManage: boolean;
  isVerifying: boolean;
  verifyFailure?: { message: string; diagnostics: VerifyDiagnostic[] };
  onVerify: () => void;
  onSetPrimary: () => void;
  onDelete: () => void;
}

function DomainRow({
  domain,
  canManage,
  isVerifying,
  verifyFailure,
  onVerify,
  onSetPrimary,
  onDelete,
}: DomainRowProps) {
  return (
    <li className="domain-row">
      <div className="domain-row__head">
        <div className="domain-row__title">
          <code className="domain-row__hostname">{domain.domain}</code>
          {domain.primary && <Badge tone="info">Primary</Badge>}
          <StatusBadge verified={domain.verified} ssl={domain.ssl_status} />
        </div>
        {canManage && (
          <div className="domain-row__actions">
            {!domain.verified && (
              <Button
                variant="secondary"
                size="sm"
                onClick={onVerify}
                disabled={isVerifying}
                loading={isVerifying}
              >
                {isVerifying ? 'Verifying…' : 'Verify'}
              </Button>
            )}
            {domain.routable && !domain.primary && (
              <Button variant="secondary" size="sm" onClick={onSetPrimary}>
                Set as primary
              </Button>
            )}
            <Button variant="danger" size="sm" onClick={onDelete}>
              Remove
            </Button>
          </div>
        )}
      </div>

      {!domain.verified && domain.verification_instructions && (
        <div className="domain-row__instructions">
          <p className="domain-row__dns-heading">
            Add the following DNS records to <code>{domain.domain}</code>, then click Verify:
          </p>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Value</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domain.verification_instructions.map(record => (
                <DnsRecordRow key={record.id} record={record} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {verifyFailure && (
        <VerifyDiagnosticsPanel
          message={verifyFailure.message}
          diagnostics={verifyFailure.diagnostics}
        />
      )}

      {domain.verified && domain.idp_callback_urls && domain.idp_callback_urls.length > 0 && (
        <IdpCallbackUrls domain={domain.domain} urls={domain.idp_callback_urls} />
      )}
    </li>
  );
}

function VerifyDiagnosticsPanel({
  message,
  diagnostics,
}: {
  message: string;
  diagnostics: VerifyDiagnostic[];
}) {
  return (
    <div className="domain-row__diagnostics">
      <p className="domain-row__diagnostics-heading">
        Verification failed: {message}.{' '}
        <a href={CUSTOM_DOMAINS_DOCS_URL} target="_blank" rel="noopener noreferrer">
          Read the custom-domains guide →
        </a>
      </p>
      {diagnostics.length > 0 && (
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Record</TableHeaderCell>
              <TableHeaderCell>Expected</TableHeaderCell>
              <TableHeaderCell>Current</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diagnostics.map(d => (
              <DiagnosticRow key={`${d.type}:${d.host}`} diagnostic={d} />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

function DiagnosticRow({ diagnostic }: { diagnostic: VerifyDiagnostic }) {
  const matched =
    !diagnostic.error &&
    diagnostic.current.length > 0 &&
    diagnostic.expected.some(e => diagnostic.current.includes(e));
  return (
    <TableRow>
      <TableCell>
        <div className="domain-row__diagnostics-record">
          <span className="domain-row__dns-type">{diagnostic.type}</span>
          <code>{diagnostic.host}</code>
        </div>
      </TableCell>
      <TableCell>
        {diagnostic.expected.map(value => (
          <code key={value} className="domain-row__diagnostics-value">
            {value}
          </code>
        ))}
      </TableCell>
      <TableCell>
        {diagnostic.error ? (
          <span className="domain-row__diagnostics-missing">Lookup failed: {diagnostic.error}</span>
        ) : diagnostic.current.length === 0 ? (
          <span className="domain-row__diagnostics-missing">No record found</span>
        ) : (
          <div className="domain-row__diagnostics-current">
            {diagnostic.current.map((value, idx) => (
              <code
                key={`${value}-${idx}`}
                className={
                  matched
                    ? 'domain-row__diagnostics-value domain-row__diagnostics-value--matched'
                    : 'domain-row__diagnostics-value domain-row__diagnostics-value--mismatched'
                }
              >
                {value}
              </code>
            ))}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function IdpCallbackUrls({ domain, urls }: { domain: string; urls: IdpCallbackUrl[] }) {
  return (
    <div className="domain-row__instructions">
      <p className="domain-row__dns-heading">
        Register these callback URLs with each IdP (Google Cloud Console, GitHub OAuth App, etc.) so
        end users on <code>{domain}</code> can complete the OAuth flow:
      </p>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Provider</TableHeaderCell>
            <TableHeaderCell>Authorized redirect URI</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {urls.map(({ provider, url }) => (
            <CallbackUrlRow key={provider} provider={provider} url={url} />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CallbackUrlRow({ provider, url }: { provider: string; url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be unavailable in non-secure contexts */
    }
  };
  return (
    <TableRow>
      <TableCell className="domain-row__dns-type">{provider}</TableCell>
      <TableCell>
        <div className="domain-row__dns-cell">
          <code>{url}</code>
          <button
            type="button"
            className="domain-row__copy"
            aria-label={`Copy ${provider} callback URL`}
            onClick={copy}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function DnsRecordRow({ record }: { record: VerificationRecord }) {
  const [copied, setCopied] = useState<'host' | 'value' | null>(null);

  const copy = async (field: 'host' | 'value', text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(field);
      window.setTimeout(() => setCopied(c => (c === field ? null : c)), 1500);
    } catch {
      /* clipboard may be unavailable in non-secure contexts */
    }
  };

  return (
    <TableRow>
      <TableCell className="domain-row__dns-type">{record.type}</TableCell>
      <TableCell>
        <div className="domain-row__dns-cell">
          <code>{record.host}</code>
          <button
            type="button"
            className="domain-row__copy"
            aria-label={`Copy ${record.type} name`}
            onClick={() => copy('host', record.host)}
          >
            {copied === 'host' ? 'Copied' : 'Copy'}
          </button>
        </div>
      </TableCell>
      <TableCell>
        <div className="domain-row__dns-cell">
          <code>{record.value}</code>
          <button
            type="button"
            className="domain-row__copy"
            aria-label={`Copy ${record.type} value`}
            onClick={() => copy('value', record.value)}
          >
            {copied === 'value' ? 'Copied' : 'Copy'}
          </button>
        </div>
        {record.note && <p className="domain-row__dns-note">{record.note}</p>}
      </TableCell>
    </TableRow>
  );
}

function StatusBadge({ verified, ssl }: { verified: boolean; ssl: ProjectDomain['ssl_status'] }) {
  let tone: BadgeTone = 'warning';
  let label = 'Pending verification';

  if (verified && ssl === 'active') {
    tone = 'success';
    label = 'Active';
  } else if (verified && ssl === 'provisioning') {
    tone = 'info';
    label = 'Provisioning SSL…';
  } else if (verified && ssl === 'failed') {
    tone = 'danger';
    label = 'SSL failed';
  } else if (verified) {
    tone = 'info';
    label = 'Verified';
  }

  return <Badge tone={tone}>{label}</Badge>;
}

interface CreateDomainModalProps {
  onClose: () => void;
  onCreate: (data: { domain: string }) => Promise<ProjectDomain>;
  isSubmitting: boolean;
}

function CreateDomainModal({ onClose, onCreate, isSubmitting }: CreateDomainModalProps) {
  const [domain, setDomain] = useState('');
  const [created, setCreated] = useState<ProjectDomain | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!domain.trim()) return;
    setError(null);
    try {
      const result = await onCreate({ domain: domain.trim() });
      setCreated(result);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal domain-modal"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-domain-title"
      >
        <div className="modal__header">
          <h2 id="add-domain-title">Add custom domain</h2>
          <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>

        {created ? (
          <CreateDomainSuccessStep domain={created} onClose={onClose} />
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="modal__body">
              <p className="domain-modal__intro">
                Point a subdomain you own (e.g. <code>auth.yourcompany.com</code>) at Pryma Auth.
                We'll walk you through the DNS records and provision SSL after verification.
              </p>

              <Field
                label="Domain"
                htmlFor="domain-input"
                hint="Use a subdomain, not your root domain. You'll add a CNAME and a TXT record — the CNAME routes traffic to us, the TXT proves ownership."
              >
                <Input
                  id="domain-input"
                  type="text"
                  value={domain}
                  onChange={e => setDomain(e.target.value)}
                  placeholder="auth.yourcompany.com"
                  autoComplete="off"
                  autoFocus
                />
              </Field>

              {error && <p className="domain-modal__error">{error}</p>}
            </div>

            <div className="modal__footer">
              <Button type="button" variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !domain.trim()}
                loading={isSubmitting}
              >
                {isSubmitting ? 'Adding…' : 'Add domain'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function CreateDomainSuccessStep({
  domain,
  onClose,
}: {
  domain: ProjectDomain;
  onClose: () => void;
}) {
  return (
    <>
      <div className="modal__body">
        <p className="domain-modal__success">
          ✓ <code>{domain.domain}</code> added.
        </p>
        <p className="domain-modal__intro">
          Add these DNS records at your DNS provider, then click <strong>Verify</strong> on the row.
          SSL is provisioned automatically once verification succeeds.
        </p>

        {domain.verification_instructions && (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Type</TableHeaderCell>
                <TableHeaderCell>Name</TableHeaderCell>
                <TableHeaderCell>Value</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {domain.verification_instructions.map(record => (
                <DnsRecordRow key={record.id} record={record} />
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <div className="modal__footer">
        <Button type="button" onClick={onClose}>
          Done
        </Button>
      </div>
    </>
  );
}
